import React, { useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import {
  CheckAvailabilityCarouselMessage,
  CollectionName,
} from '@tomis-tech/types';

import { themeBlue } from '../consts';
import { stripHtml } from '../utils';
import { useParent, useSession } from '../context';
import { Overlay } from './styled-components';
import CloseIcon from './icons/CloseIcon';
import { getLinkClickUserActionMessage } from '../messages';
import { GA4Event } from '../../tracking/event-types';
import { trackGA4Event } from '../../tracking';
import { logError } from '.././sentry-iframe';
export interface CheckAvailabilityCarouselProps {
  message: CheckAvailabilityCarouselMessage;
}

const CheckAvailabilityCarousel: React.FC<CheckAvailabilityCarouselProps> = ({
  message,
}) => {
  const { conversation, site } = useSession();
  const { goToUrl, pageView } = useParent();
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAllTimes, setShowAllTimes] = useState(false);

  const {
    availableTimes,
    currency,
    error,
    productDescription,
    // productId,
    productImage,
    productName,
    productPrice,
    // productUrl,
  } = message.data[currentCardIndex];

  const { checkAvailability } = site.botConfig;

  /** Analytics tracking, then redirect to URL */
  function handleLinkClick(url: string) {
    const linkClickMessage = getLinkClickUserActionMessage(url, pageView);

    trackGA4Event(GA4Event.CheckAvailabilityLinkClick, { link_url: url }, true);

    conversation.ref
      .collection(CollectionName.Messages)
      .add(linkClickMessage)
      .then(() => {
        // wait until message is stored before redirect
        goToUrl(url);
      })
      .catch(error => {
        logError(error, { message: 'Error saving link click message' });
        goToUrl(url);
      });
  }

  return (
    <>
      <Container>
        <CardContainer>
          {Boolean(productImage) && <Image color={productImage} />}
          <TitleAndDescriptionContainer>
            <TitleAndPriceContainer>
              <ProductName>{productName}</ProductName>
              <PriceContainer>
                {Boolean(productPrice) && (
                  <>
                    <PriceLabelText>starting at</PriceLabelText>
                    {`${productPrice} ${currency}`}
                  </>
                )}
              </PriceContainer>
            </TitleAndPriceContainer>
            {Boolean(productDescription) && (
              <DescriptionText>{stripHtml(productDescription)}</DescriptionText>
            )}
          </TitleAndDescriptionContainer>
          <AvailableTimesButtonsContainer>
            {availableTimes.map((time, index) => {
              // limit two available times showing
              if (index > 1) return null;

              /** Some tour do not include a time. In that case, only show the date */
              const formattedTime = moment(time.dateTime).format(
                time.dateTime.includes('T') ? 'ddd, MMM D - LT' : 'ddd, MMM D'
              );

              return (
                <AvailableTimesButtons
                  onClick={() => handleLinkClick(time.bookingUrl)}
                  key={`${time.bookingUrl}-${time.dateTime}-${time.quantity}`}
                >
                  {formattedTime}
                </AvailableTimesButtons>
              );
            })}
            {availableTimes.length === 0 &&
              (error ? (
                <AvailableTimesNoneText style={{ color: 'red' }}>
                  Error checking availability
                </AvailableTimesNoneText>
              ) : (
                <AvailableTimesNoneText>
                  No Availability for Selected Date Range
                </AvailableTimesNoneText>
              ))}
            {availableTimes.length > 2 && (
              <LinkText color={themeBlue} onClick={() => setShowAllTimes(true)}>
                more available times
              </LinkText>
            )}
          </AvailableTimesButtonsContainer>
          {checkAvailability.allProductsUrl && (
            <AllProductsUrlContainer>
              <LinkText
                color={themeBlue}
                onClick={() =>
                  handleLinkClick(checkAvailability.allProductsUrl)
                }
                style={{ fontSize: '12px', padding: '5px' }}
              >
                view all times and tours
              </LinkText>
            </AllProductsUrlContainer>
          )}
        </CardContainer>
        {message.data.length > 1 && (
          <ArrowNavigatorContainer>
            <button
              disabled={currentCardIndex === 0}
              onClick={() => setCurrentCardIndex(currentCardIndex - 1)}
            >
              {'<'}
            </button>
            <button
              disabled={
                message.data.length === 1 ||
                currentCardIndex + 1 === message.data.length
              }
              onClick={() => setCurrentCardIndex(currentCardIndex + 1)}
            >
              {'>'}
            </button>
            <ArrowNavigatorText>
              {' '}
              {`${currentCardIndex + 1} of ${message.data.length}`}
            </ArrowNavigatorText>
          </ArrowNavigatorContainer>
        )}
      </Container>
      {showAllTimes && (
        <AvailableTimesOverlay>
          <OverlayCloseIconContainer onClick={() => setShowAllTimes(false)}>
            <CloseIcon />
          </OverlayCloseIconContainer>
          <OverlayTitle>{productName}</OverlayTitle>
          {availableTimes.map(time => {
            /** Some tour do not include a time. In that case, only show the date */
            const formattedTime = moment(time.dateTime).format(
              time.dateTime.includes('T') ? 'ddd, MMM D - LT' : 'ddd, MMM D'
            );
            return (
              <AvailableTimesButtons
                onClick={() => handleLinkClick(time.bookingUrl)}
                key={formattedTime}
                style={{ border: 'none' }}
              >
                {formattedTime}
              </AvailableTimesButtons>
            );
          })}
        </AvailableTimesOverlay>
      )}
    </>
  );
};

export default CheckAvailabilityCarousel;

// Styled Components
const ArrowNavigatorContainer = styled.div`
  align-items: center;
  color: ${themeBlue};
  display: flex;
  flex-direction: column;
  font-size: 20px;
  justify-content: center;
  margin-left: 10px;
  min-width: 35px;
  width: 40px;
`;

const ArrowNavigatorText = styled.p`
  font-size: 12px;
  text-align: center;
  color: darkgray;
  margin-top: 10px;
`;

const AllProductsUrlContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #eee;
`;

const AvailableTimesButtons = styled.button`
  background-color: white;
  border: 1px solid ${themeBlue};
  border-radius: 15px;
  color: ${themeBlue};
  cursor: pointer;
  margin-bottom: 5px;
  padding: 5px;
  width: 100%;
  &:hover {
    background-color: ${themeBlue};
    color: white;
  }
`;

const AvailableTimesButtonsContainer = styled.div`
  border-bottom: 1px solid lightgray;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  padding: 0px 20px 10px;
`;

const LinkText = styled.p`
  color: ${themeBlue};
  cursor: pointer;
  font-size: 14px;
  text-align: center;
  &:hover {
    text-decoration: underline;
  }
`;

const AvailableTimesNoneText = styled.p`
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const AvailableTimesOverlay = styled(Overlay)`
  display: flex;
  flex-direction: column;
  margin-left: -15px;
  overflow: auto;
  top: 0px;
`;

const CardContainer = styled.div`
  border-radius: 10px;
  box-shadow: 1px 1px 8px -3px black;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin: 10px;
  max-width: 250px;
  // min-height: 380px;
  overflow: hidden;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const DescriptionText = styled.p`
  color: darkgray;
  font-size: 12px;
  padding-top: 10px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
`;

const Image = styled.div`
  align-items: center;
  background-image: url(${props => props.color});
  background-size: cover;
  color: lightgray;
  display: flex;
  font-size: 12px;
  height: 150px;
  justify-content: center;
  max-height: 120px;
  overflow: hidden;
  width: auto;
`;

const OverlayCloseIconContainer = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
`;

const OverlayTitle = styled.div`
  text-align: center;
  margin-bottom: 15px;
`;

const TitleAndDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
`;
const TitleAndPriceContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const ProductName = styled.p`
  font-weight: bold;
  text-align: center;
  margin-bottom: 4px;
`;

const PriceContainer = styled.div`
  align-items: baseline;
  display: flex;
  flex-direction: row;
`;

const PriceLabelText = styled.p`
  color: darkgray;
  font-size: 12px;
  font-style: italic;
  font-weight: 300;
  margin-right: 5px;
  text-align: right;
`;
// END Styled Components
